import { viteEnv } from './viteEnv';

/**
 * Product analytics for the Reader-to-Revenue Score funnel.
 *
 * Active when VITE_POSTHOG_KEY is set. Until then, events are no-ops
 * (not claimed as validated).
 */

export type AnalyticsEvent = 'score_start' | 'score_complete' | 'audit_request' | 'proof_lightbox_open' | 'proof_score_cta_click';
type Props = Record<string, string | number | boolean | null | undefined>;
const SCORE_START_KEY = 'authorLeverage:tracked:score_start';
const SCORE_COMPLETE_KEY = 'authorLeverage:tracked:score_complete';
const AUDIT_MAILTO_KEY = 'authorLeverage:tracked:audit_mailto';
const AUDIT_FORM_KEY = 'authorLeverage:tracked:audit_form';
function posthogKey(): string | undefined {
  return viteEnv('VITE_POSTHOG_KEY');
}
function posthogHost(): string {
  return viteEnv('VITE_POSTHOG_HOST') || 'https://us.i.posthog.com';
}

/** True when PostHog capture is configured. */
export function isAnalyticsConfigured(): boolean {
  return Boolean(posthogKey());
}
export function track(event: AnalyticsEvent, properties: Props = {}): void {
  const key = posthogKey();
  if (!key || typeof window === 'undefined') return;
  const distinctId = window.localStorage.getItem('authorLeverage:anonId') || (() => {
    const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem('authorLeverage:anonId', id);
    return id;
  })();
  void fetch(`${posthogHost().replace(/\/$/, '')}/capture/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      api_key: key,
      event,
      distinct_id: distinctId,
      properties: {
        ...properties,
        $lib: 'authorleverage-web',
        path: window.location.pathname
      },
      timestamp: new Date().toISOString()
    }),
    keepalive: true
  }).catch(() => {

    // Analytics must never block the product path.
  });
}

/** Fire once per browser session when the diagnostic opens. */
export function trackScoreStart(): void {
  if (typeof window === 'undefined') return;
  if (window.sessionStorage.getItem(SCORE_START_KEY)) return;
  window.sessionStorage.setItem(SCORE_START_KEY, '1');
  track('score_start');
}

/** Fire once per completed score payload (session-scoped). */
export function trackScoreComplete(totalScore?: number, band?: string): void {
  if (typeof window === 'undefined') return;
  const fingerprint = `${totalScore ?? ''}:${band ?? ''}`;
  if (window.sessionStorage.getItem(SCORE_COMPLETE_KEY) === fingerprint) return;
  window.sessionStorage.setItem(SCORE_COMPLETE_KEY, fingerprint);
  track('score_complete', {
    total_score: totalScore,
    band
  });
}
export function trackAuditRequest(source: 'form' | 'mailto_fallback'): void {
  if (typeof window === 'undefined') return;
  // Once per session per source — Prefer email / mailto fallback can be
  // clicked repeatedly for recovery without inflating conversion counts.
  const key = source === 'form' ? AUDIT_FORM_KEY : AUDIT_MAILTO_KEY;
  if (window.sessionStorage.getItem(key)) return;
  window.sessionStorage.setItem(key, '1');
  track('audit_request', {
    source
  });
}

/** Fired when a proof-stage lightbox opens. */
export function trackProofLightboxOpen(step: string | number, stepId?: string): void {
  track('proof_lightbox_open', {
    proof_step: step,
    proof_step_id: stepId
  });
}

/** Fired from the post-proof Score CTA bridge. */
export function trackProofScoreCtaClick(): void {
  track('proof_score_cta_click', {
    placement: 'after_author_context'
  });
}